import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Printer, Download, Loader2, AlertCircle } from 'lucide-react';
import comprobanteRetencionIVATemplateHtml from './ComprobanteRetencionIVATemplate';

const MAX_RETRIES = 50;

const transformData = (recaudosData) => {
  if (!recaudosData || recaudosData.length === 0) return null;

  const now = new Date();
  const fechaEmision = now.toLocaleDateString('es-VE', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });

  const mesActual = String(now.getMonth() + 1).padStart(2, '0');
  const anioActual = String(now.getFullYear());
  const nroComprobante = `${anioActual}${mesActual}${String(now.getDate()).padStart(2, '0')}${String(recaudosData.length).padStart(4, '0')}`;

  const operaciones = recaudosData.map((f, index) => {
    const fechaFactura = f.fecha_creacion
      ? new Date(f.fecha_creacion).toLocaleDateString('es-VE', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        })
      : '';

    const totalComprasIVA = f.totalConIva || (f.baseImponible || 0) + (f.iva || 0);
    const alicuota = '16,00%';
    const pctRet = f.porcentajeRetencion ? f.porcentajeRetencion + '%' : '';

    return {
      nro: index + 1,
      fecha: fechaFactura,
      numeroComprobante: nroComprobante,
      numeroFactura: f.numero_cotizacion || `COT-${f.id}`,
      totalComprasIVA: totalComprasIVA.toFixed(2),
      baseImponible: (f.baseImponible || 0).toFixed(2),
      porcentajeAlicuota: alicuota,
      impuestoIVA: (f.iva || 0).toFixed(2),
      porcentajeRetencion: pctRet,
      ivaRetenido: (f.montoRetencionIva || 0).toFixed(2)
    };
  });

  const totales = operaciones.reduce((acc, op) => ({
    totalComprasIVA: acc.totalComprasIVA + parseFloat(op.totalComprasIVA || 0),
    baseImponible: acc.baseImponible + parseFloat(op.baseImponible || 0),
    impuestoIVA: acc.impuestoIVA + parseFloat(op.impuestoIVA || 0),
    ivaRetenido: acc.ivaRetenido + parseFloat(op.ivaRetenido || 0)
  }), { totalComprasIVA: 0, baseImponible: 0, impuestoIVA: 0, ivaRetenido: 0 });

  Object.keys(totales).forEach(k => {
    totales[k] = totales[k].toFixed(2);
  });

  const depositoTotal = recaudosData.reduce((sum, f) => sum + (f.deposito || 0), 0);

  const primerItem = recaudosData[0];

  return {
    agente: {
      nombre: 'ELECTROSOLUCIONES GAMAN C.A',
      direccion: 'CALLE ROSCIO EDIF RESIDENCIAS MARIANA PISO PB LOCAL 3 SECTOR SAN JUAN DE LOS MORROS GUARICO ZONA POSTAL 2301',
      rif: 'J-411293229'
    },
    sujetoRetenido: {
      nombre: primerItem.cliente_nombre || primerItem.empresa_nombre || '',
      rif: primerItem.empresa_rif || ''
    },
    comprobante: {
      numero: nroComprobante,
      fechaEmision: fechaEmision,
      periodoAnio: anioActual,
      periodoMes: mesActual,
      nroComprobante: nroComprobante
    },
    operaciones: operaciones,
    totales: totales,
    footer: {
      netoAPagar: depositoTotal.toFixed(2),
      fechaEntrega: fechaEmision
    }
  };
};

const ComprobanteRetencionIVAPrintModal = ({ onClose, data }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const datosInyectados = useRef(false);
  const retryCount = useRef(0);
  const htmlContent = comprobanteRetencionIVATemplateHtml;

  const datosComprobante = data ? transformData(data) : null;

  const inyectarDatos = useCallback(() => {
    if (!iframeRef.current || datosInyectados.current || !datosComprobante) return;
    try {
      const win = iframeRef.current.contentWindow;
      if (win && typeof win.renderComprobanteRetencionIVA === 'function') {
        win.renderComprobanteRetencionIVA(datosComprobante);
        datosInyectados.current = true;
        setLoading(false);
      } else if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        setTimeout(inyectarDatos, 100);
      } else {
        setError('No se pudo cargar el comprobante (tiempo de espera agotado)');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error al inyectar datos:', err);
      setError('Error al renderizar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  useEffect(() => {
    if (!datosComprobante) {
      setError('No hay datos de recaudos para generar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  const handleIframeLoad = useCallback(() => {
    inyectarDatos();
  }, [inyectarDatos]);

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        iframeRef.current.contentWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gray-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Printer className="text-gray-600" />
              Comprobante Retención IVA
            </h2>
            <p className="text-sm text-gray-500">Vista previa del comprobante</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                >
                  <Download size={18} /> Descargar PDF
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
                >
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium flex items-center gap-2"
            >
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando documento...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">Error al cargar datos</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Comprobante Retención IVA"
          />
        </div>
      </div>
    </div>
  );
};

export default ComprobanteRetencionIVAPrintModal;
